import React, { useEffect, useState } from 'react';
import { caregiverProfilesAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShieldCheck, Warning, LinkSimple, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

/** Supervisor diagnostic: is this caregiver's login linked to the shifts assigned to them? */
const CaregiverLinkCheck = ({ profileId, onRelinked }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setData((await caregiverProfilesAPI.linkCheck(profileId)).data); } catch { /* hidden on error */ } };
  useEffect(() => { load(); }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const relink = async (fromIds, targetUserId) => {
    const n = data.buckets.filter(b => fromIds.includes(b.caregiver_id)).reduce((s, b) => s + b.visits, 0);
    if (!window.confirm(`Move ${n} visit(s) to this caregiver's current login so they appear in their portal?`)) return;
    setBusy(true);
    try {
      const r = await caregiverProfilesAPI.relinkShifts(profileId, { from_ids: fromIds, target_user_id: targetUserId || undefined });
      toast.success(`Relinked ${r.data.visits_relinked} visits and ${r.data.assignments_relinked} assignments`);
      await load(); onRelinked?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Relink failed'); } finally { setBusy(false); }
  };

  if (data.ok) {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="link-check-ok">
        <ShieldCheck weight="fill" className="w-4 h-4" /> Login linked · {data.buckets.find(b => b.is_linked_login)?.upcoming ?? 0} upcoming shifts visible in their portal{data.warnings?.length ? ` · ${data.warnings[0]}` : ''}
        <button onClick={load} className="ml-auto text-emerald-700/70 hover:text-emerald-900" title="Re-check"><ArrowsClockwise className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <Card className="rounded-2xl border-amber-200 bg-amber-50" data-testid="link-check-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Warning weight="fill" className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Shifts may not be showing in this caregiver's portal</p>
            <ul className="text-sm text-amber-900/90 list-disc pl-4 mt-1 space-y-0.5">{data.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
        </div>
        <div className="space-y-1.5">
          {data.buckets.filter(b => b.visits > 0 || b.is_linked_login).map(b => (
            <div key={b.caregiver_id} className="flex items-center gap-3 rounded-xl bg-white/80 border border-amber-200/60 px-3 py-2 text-sm" data-testid={`link-bucket-${b.caregiver_id}`}>
              <div className="flex-1 min-w-0">
                <div className="truncate">{b.user ? `${b.user.name} <${b.user.email}>` : b.is_profile_id ? 'Profile id (no login)' : `Unknown id ${b.caregiver_id.slice(0, 8)}…`}
                  {b.is_linked_login && <Badge className="ml-2 rounded-full text-[10px] bg-emerald-100 text-emerald-800">current login</Badge>}
                  {b.user && b.user.is_active === false && <Badge variant="outline" className="ml-2 rounded-full text-[10px]">inactive</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{b.visits} visits · {b.upcoming} upcoming</p>
              </div>
              {!b.is_linked_login && b.visits > 0 && data.linked_user && (
                <Button size="sm" className="rounded-xl gap-1" disabled={busy} onClick={() => relink([b.caregiver_id])} data-testid={`relink-${b.caregiver_id}`}><LinkSimple className="w-3.5 h-3.5" /> Move to current login</Button>
              )}
              {!b.is_linked_login && b.user && b.user.is_active && !data.linked_user && (
                <Button size="sm" variant="outline" className="rounded-xl gap-1" disabled={busy} onClick={() => relink(data.buckets.filter(x => x.caregiver_id !== b.caregiver_id).map(x => x.caregiver_id), b.caregiver_id)} data-testid={`use-login-${b.caregiver_id}`}>Use this login</Button>
              )}
            </div>
          ))}
        </div>
        {data.stray_ids.length > 1 && data.linked_user && (
          <Button className="rounded-xl gap-1.5" disabled={busy} onClick={() => relink(data.stray_ids)} data-testid="relink-all-btn"><LinkSimple className="w-4 h-4" /> Move all shifts to current login</Button>
        )}
      </CardContent>
    </Card>
  );
};

export default CaregiverLinkCheck;
